import React, { useCallback, useMemo, useRef } from 'react';
import { STRINGS, formatCurrency, formatNumber } from '../utils/constants';

// Sentinel value used in place of a service id when "All" is active.
export const ALL_SERVICES = null;

const Chip = React.forwardRef(({ chip, isActive, onSelect, onKey }, ref) => {
  const color = chip.color || '#3b82f6';
  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      className={`service-chip ${isActive ? 'is-active' : ''}`}
      style={{ '--chip-color': color }}
      onClick={() => onSelect(chip.id)}
      onKeyDown={(e) => onKey(e, chip.id)}
    >
      <span className="service-chip__dot" aria-hidden="true" />
      <span className="service-chip__body">
        <span className="service-chip__name">{chip.name}</span>
        {chip.meta && <span className="service-chip__meta">{chip.meta}</span>}
      </span>
    </button>
  );
});

const ServiceFilterStrip = ({ data, activeServiceId, onChange }) => {
  const stripRef = useRef(null);
  const chipsRef = useRef([]);

  const chips = useMemo(() => {
    if (!data) return [];

    const aggregatedMeta = (() => {
      const parts = [];
      if (data.totalVisitors > 0) parts.push(`${formatNumber(data.totalVisitors)} ${STRINGS.visitors}`);
      if (data.totalRevenue > 0) parts.push(formatCurrency(data.totalRevenue));
      return parts.join(' • ');
    })();

    const list = [
      {
        id: ALL_SERVICES,
        name: STRINGS.allServices,
        color: '#3b82f6',
        meta: aggregatedMeta || null,
      },
    ];

    (data.serviceSummary || []).forEach((srv) => {
      const parts = [];
      if (srv.totalVisitors > 0) parts.push(`${formatNumber(srv.totalVisitors)} ${STRINGS.visitors}`);
      if (srv.totalRevenue > 0) parts.push(formatCurrency(srv.totalRevenue));
      list.push({
        id: srv.id,
        name: srv.name,
        color: srv.color,
        meta: parts.join(' • ') || null,
      });
    });

    return list;
  }, [data]);

  // Keyboard tab navigation (arrow keys cycle, Home/End jump).
  const handleKey = useCallback((event, currentId) => {
    const idx = chips.findIndex((c) => c.id === currentId);
    if (idx === -1) return;
    let next = idx;
    if (event.key === 'ArrowRight') next = idx - 1; // RTL: right ⇒ previous
    else if (event.key === 'ArrowLeft') next = idx + 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = chips.length - 1;
    else return;
    event.preventDefault();
    const wrapped = (next + chips.length) % chips.length;
    const nextChip = chips[wrapped];
    onChange(nextChip.id);
    chipsRef.current[wrapped]?.focus();
  }, [chips, onChange]);

  if (!data || chips.length === 0) return null;

  return (
    <div
      className="service-strip glass-card mb-1"
      role="tablist"
      aria-label={STRINGS.servicesFilter}
    >
      <div className="service-strip__scroll" ref={stripRef}>
        {chips.map((chip, idx) => (
          <Chip
            key={chip.id === ALL_SERVICES ? 'all' : chip.id}
            ref={(el) => { chipsRef.current[idx] = el; }}
            chip={chip}
            isActive={activeServiceId === chip.id}
            onSelect={onChange}
            onKey={handleKey}
          />
        ))}
      </div>
    </div>
  );
};

export default ServiceFilterStrip;
